/**
 * EditStayDialog — «Canviar dates, habitació o preu» des de la fitxa de reserva (R4, runbook 6).
 *
 * Només ADMIN. `PATCH /reservations/:id` amb només els camps canviats (`data_entrada`,
 * `data_sortida`, `unit_id`, `preu_total`): el backend ho envia primer a Beds24 i, si falla (502)
 * o la unitat és ocupada (409), no desa res — el diàleg es queda obert amb el missatge.
 *
 * Reserves d'OTA: només la unitat. Les dates i el preu es canvien a l'extranet del canal.
 * Reserves directes: també «Cancel·lar la reserva» → `DELETE /reservations/:id` amb `{ motiu }`
 * (la mateixa ruta que fa servir `ReservationsService.cancel()` del frontend).
 * El desat és `useSaveStay` (`hooks/useSaveStay.ts`), compartit amb la graella (runbook 7, F3).
 *
 * Bloqueig a l'habitació (`plans/reservation-move-lock.md` §4 F.4): si la reserva està bloquejada
 * (`metadata.moveLockReason` de `GET /reservations/:id`) i es tria una altra habitació, surt el motiu
 * i cal marcar «He llegit el motiu…»; llavors el `PATCH` porta `acknowledge_move_lock: true`.
 * Canviar només dates o preu no demana res.
 *
 * Preu (`plans/preus-en-crear-reserves.md`, F2): si s'escriu un preu NOU que no es pot partir en base
 * i quota al cèntim, surt l'avís amb els dos preus bons més propers (`PriceVatNotice`). No bloqueja.
 * El preu que la reserva ja tenia no avisa mai (decisió 12).
 *
 * Hora de sortida pactada (portes v1, sprint 06 §4): `Select` de 12:00 a 20:00 cada 30 min (buit =
 * 12:00). No va a Beds24: si canvia, «Desar» fa primer `PATCH /reservations/:id/agreed-checkout` i,
 * si també hi ha dates, habitació o preu, el `PATCH` de sempre. També a les reserves d'OTA.
 */

import { useState } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSaveStay, type StayPatch } from '@/hooks/useSaveStay';
import { useAgreedCheckoutMutation } from '@/hooks/useAgreedCheckoutMutation';
import {
  AGREED_CHECKOUT_NONE,
  agreedCheckoutChange,
  agreedCheckoutOptions,
  agreedCheckoutSelectValue,
} from '@/lib/agreed-checkout';
import {
  OccupancyNotLoadedNotice,
  PriceVatNotice,
  buildUnitOptions,
  useLoadedOccupancy,
} from '@/components/reservations';
import {
  centsToInputValue,
  hasMoreThanTwoDecimals,
  priceVatNotice,
  type PriceVatNotice as PriceVatNoticeData,
} from '@/lib/pricing/price-vat-notice';
import { PRICE_TOO_MANY_DECIMALS } from '@/lib/schemas/create-reservation';
import { accommodationVatPercent } from '@/lib/pricing/vat-config';
import { toast } from 'sonner';
import { CalendarClock, Info, Loader2 } from 'lucide-react';
import { api, calendarApi, type Unit } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { cancelResultToast, type CancelToast } from '@/lib/cancel-result-toast';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// ============================================
// TYPES
// ============================================

/** Els camps de `GET /reservations/:id` que fa servir el diàleg. */
export interface StaySource {
  id: string;
  estat?: string | null;
  canal?: string | null;
  data_entrada: string;
  data_sortida: string;
  unit_id?: string | null;
  preu_total?: number | null;
  beds24_property_id?: number | null;
  /** `metadata.moveLockReason`: motiu del bloqueig a l'habitació; null = no bloquejada. */
  metadata?: { moveLockReason?: string | null } | null;
  /** Hora de sortida pactada, `"HH:mm"` de Madrid; `null` = 12:00 (portes v1). */
  agreedCheckoutTime?: string | null;
}

export type { StayPatch } from '@/hooks/useSaveStay';

export interface CancelResponse {
  message?: string;
  beds24Synced?: boolean;
  /** Fora de producció: la cancel·lació NO ha sortit cap a Beds24. A producció no hi és mai. */
  beds24Simulated?: boolean;
  locksError?: string | null;
}

// ============================================
// HELPERS (purs, provats a EditStayDialog.test.tsx)
// ============================================

/**
 * El toast de «Cancel·lar la reserva». Fora de producció la cancel·lació no surt cap a Beds24
 * (`beds24Simulated`): l'avís de la pàgina de Reserves (`cancelResultToast`). Sense la marca, el
 * de sempre: èxit amb el `message` del backend.
 */
export function stayCancelToast(data: CancelResponse | null | undefined): CancelToast {
  if (data?.beds24Simulated === true) return cancelResultToast(data);
  return { kind: 'success', title: 'Reserva cancel·lada', description: data?.message };
}

export const OTA_LOCKED_TEXT = "Es canvien a l'extranet del canal";
export const STAY_NOTICE =
  "El canvi s'envia primer a Beds24. El codi de la porta no canvia; en un canvi d'habitació pot trigar fins a 5 minuts a arribar a la porta nova.";
/** Avís en allargar o escurçar (canvi de dates sense canvi d'habitació). */
export const STAY_DATES_NOTICE =
  "El canvi s'envia primer a Beds24. El codi de la porta no canvia; la nova sortida pot trigar fins a 5 minuts a arribar a la porta.";

/**
 * Quin avís toca: si canvia l'habitació, el de la porta nova (`STAY_NOTICE`), també si alhora
 * canvien les dates; si només canvien les dates, el de la nova sortida; sense cap canvi, `STAY_NOTICE`.
 */
export function stayNoticeFor(datesChanged: boolean, unitChanged: boolean): string {
  return datesChanged && !unitChanged ? STAY_DATES_NOTICE : STAY_NOTICE;
}

/**
 * OTA = el canal és d'una d'aquestes (sense majúscules; inclou els «A-Expedia…»). La resta
 * («Direct», «setBooking JSON», «API», «GUILLEUMESMO», buit…) són directes o del PMS vell.
 * `GET /reservations/:id` no exposa `apiSource`, així que només es mira `canal`.
 */
// `\bbooking`: sense la frontera, «setBooking JSON» (PMS vell, directa) passaria per Booking.com.
export const OTA_CHANNEL_RE = /\bbooking|expedia|hotels\.com|hostelworld|orbitz|trivago|egencia|airbnb|travelocity|wotif|american express/i;

export function isOtaChannel(channel?: string | null): boolean {
  return OTA_CHANNEL_RE.test(channel ?? '');
}

export function isDirectChannel(channel?: string | null): boolean {
  return !isOtaChannel(channel);
}

/** `2026-09-20T00:00:00.000Z` → `2026-09-20` (les dates de la BD són mitjanit UTC). */
export function toDateInput(value?: string | null): string {
  return value ? String(value).slice(0, 10) : '';
}

/**
 * El número escrit, TAL COM S'HA ESCRIT: no arrodoneix. «97.184» és 97.184, i qui el fa servir ha
 * de mirar abans `priceInputError` (un preu amb més de 2 decimals no es desa mai arrodonit en silenci).
 */
export function parsePrice(value: string): number | null {
  const trimmed = value.trim().replace(',', '.');
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

export const PRICE_NOT_POSITIVE = 'El preu ha de ser més gran que 0';

/**
 * Per què un preu escrit no es pot desar, o `null` si es pot. Buit = no es toca el preu = cap error.
 * El text dels decimals és el mateix del formulari de creació.
 */
export function priceInputError(value: string): string | null {
  if (value.trim() === '') return null;
  const n = parsePrice(value);
  if (n === null || !(n > 0)) return PRICE_NOT_POSITIVE;
  return hasMoreThanTwoDecimals(n) ? PRICE_TOO_MANY_DECIMALS : null;
}

export const staySchema = z
  .object({
    data_entrada: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no vàlida'),
    data_sortida: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no vàlida'),
    unit_id: z.string(),
    preu_total: z.string(),
    /** `"HH:mm"` o `AGREED_CHECKOUT_NONE`; el `Select` només deixa triar valors bons. */
    agreed_checkout: z.string().optional(),
  })
  .refine((v) => v.data_sortida > v.data_entrada, {
    path: ['data_sortida'],
    message: "La sortida ha de ser després de l'entrada",
  })
  .refine(
    (v) => {
      if (v.preu_total.trim() === '') return true;
      const n = parsePrice(v.preu_total);
      return n !== null && n > 0;
    },
    { path: ['preu_total'], message: PRICE_NOT_POSITIVE },
  );

export type StayFormValues = z.infer<typeof staySchema>;

export function stayDefaults(source: StaySource): StayFormValues {
  return {
    data_entrada: toDateInput(source.data_entrada),
    data_sortida: toDateInput(source.data_sortida),
    unit_id: source.unit_id ?? '',
    preu_total: source.preu_total != null ? String(source.preu_total) : '',
    agreed_checkout: agreedCheckoutSelectValue(source.agreedCheckoutTime),
  };
}

export const AGREED_CHECKOUT_HINT =
  'Només al PMS: Beds24 no en sap res. Allarga o escurça el codi de les portes.';

/**
 * El `PATCH` de l'hora de sortida pactada, o `null` si no canvia. Una reserva cancel·lada no en desa
 * cap (el camp hi és desactivat).
 */
export function stayAgreedCheckoutChange(
  source: StaySource,
  values: Pick<StayFormValues, 'agreed_checkout'>,
): { time: string | null } | null {
  if ((source.estat ?? '').toUpperCase() === 'CANCELLED') return null;
  return agreedCheckoutChange(source.agreedCheckoutTime, values.agreed_checkout ?? AGREED_CHECKOUT_NONE);
}

/**
 * L'esquema del formulari d'UNA reserva: `staySchema` i, a més, un preu escrit amb més de 2
 * decimals és un error. El preu que la reserva ja tenia no es jutja mentre no es toqui (pot venir
 * del sync de Beds24 amb 3-4 decimals, i s'ha de poder desar un canvi de data igualment).
 *
 * El `<form>` porta `noValidate`: tot el que abans jutjava el navegador ho jutja aquest esquema.
 * | Validació nadiua que hi havia                         | On es valida ara                              |
 * |-------------------------------------------------------|-----------------------------------------------|
 * | `type="date"` amb una data a mig escriure (valor buit) | regex de `data_entrada` / `data_sortida`      |
 * | `type="number"` amb text que no és un número          | el navegador dona valor buit: el preu no es toca |
 * | `min="0"` del preu                                    | «El preu ha de ser més gran que 0»            |
 * | `step="0.01"` del preu                                | «El preu pot tenir 2 decimals com a màxim», aquí |
 * | casella `required` del bloqueig a l'habitació         | `moveLockAckState().blocksSave` desactiva «Desar» |
 */
export function staySchemaFor(source: StaySource) {
  const initialPrice = stayDefaults(source).preu_total;
  return staySchema.refine(
    (v) => v.preu_total === initialPrice || priceInputError(v.preu_total) !== PRICE_TOO_MANY_DECIMALS,
    { path: ['preu_total'], message: PRICE_TOO_MANY_DECIMALS },
  );
}

/**
 * El preu NOU que s'ha escrit, o `null` si el camp no s'ha tocat, és buit, no és un número, té
 * més de 2 decimals (no s'arrodoneix: no es desa), o dona el mateix preu que la reserva ja té. «No tocat» es mira sobre el text: un preu que el sync
 * de Beds24 ha desat amb 3-4 decimals (179.075) no s'ha d'enviar arrodonit només perquè s'ha obert
 * el diàleg per canviar una data.
 */
export function editedStayPrice(source: StaySource, priceInput: string | null | undefined): number | null {
  if (priceInput == null || priceInput === stayDefaults(source).preu_total) return null;
  const price = parsePrice(priceInput);
  if (price === null || hasMoreThanTwoDecimals(price)) return null;
  return price !== (source.preu_total ?? null) ? price : null;
}

export interface StayPriceFeedback {
  /** «El preu pot tenir 2 decimals com a màxim», o `null`. Amb error, «Desar» queda desactivat. */
  error: string | null;
  /** L'avís de l'IVA amb els dos preus propers, o `null`. Mai bloqueja. */
  notice: PriceVatNoticeData | null;
}

/**
 * El que el formulari ha de dir sota el camp de preu, en viu. És tota la decisió del component:
 * només per a una reserva directa i no cancel·lada (les altres no poden canviar el preu), només
 * per a un preu NOU (decisió 12: el que ja hi era no avisa), i amb el tipus d'IVA de la propietat.
 */
export function stayPriceFeedback(
  source: StaySource,
  priceInput: string | null | undefined,
  vatPercent: number = accommodationVatPercent(source.beds24_property_id),
): StayPriceFeedback {
  const editable = isDirectChannel(source.canal) && (source.estat ?? '').toUpperCase() !== 'CANCELLED';
  if (!editable || priceInput == null || priceInput === stayDefaults(source).preu_total) {
    return { error: null, notice: null };
  }
  if (priceInputError(priceInput) === PRICE_TOO_MANY_DECIMALS) {
    return { error: PRICE_TOO_MANY_DECIMALS, notice: null };
  }
  return { error: null, notice: priceVatNotice(editedStayPrice(source, priceInput), vatPercent) };
}

/** Només els camps canviats; d'una reserva d'OTA, només la unitat. */
export function buildStayPatch(
  source: StaySource,
  values: StayFormValues,
  isDirect: boolean,
): StayPatch {
  const initial = stayDefaults(source);
  const patch: StayPatch = {};

  if (values.unit_id && values.unit_id !== initial.unit_id) patch.unit_id = values.unit_id;
  if (!isDirect) return patch;

  if (values.data_entrada !== initial.data_entrada) patch.data_entrada = values.data_entrada;
  if (values.data_sortida !== initial.data_sortida) patch.data_sortida = values.data_sortida;

  const price = editedStayPrice(source, values.preu_total);
  if (price !== null) patch.preu_total = price;

  return patch;
}

export const MOVE_LOCK_ACK_TEXT = 'He llegit el motiu i la vull moure igualment';

export function moveLockNoticeText(reason: string): string {
  return `Aquesta reserva està bloquejada a la seva habitació: ${reason}`;
}

/** El motiu del 409 del backend («Reserva bloquejada: «{motiu}». Per moure-la…»), si n'és un. */
export function moveLockReasonFromError(message: string | null | undefined): string | null {
  const match = /bloquejada: «([\s\S]+)»\./.exec(message ?? '');
  return match ? match[1] : null;
}

/**
 * Cal la casella? Només si la reserva està bloquejada i el canvi mou d'habitació. `blocksSave` =
 * «Desar» desactivat fins que es marqui.
 */
export function moveLockAckState(
  lockReason: string | null | undefined,
  unitChanged: boolean,
  acknowledged: boolean,
): { required: boolean; blocksSave: boolean } {
  const required = !!lockReason && unitChanged;
  return { required, blocksSave: required && !acknowledged };
}

/** Afegeix `acknowledge_move_lock` al `PATCH` quan mou d'habitació una reserva bloquejada, confirmada. */
export function withMoveLockAck(patch: StayPatch, lockReason: string | null | undefined, acknowledged: boolean): StayPatch {
  return patch.unit_id && lockReason && acknowledged ? { ...patch, acknowledge_move_lock: true } : patch;
}

export interface MoveLockNoticeProps {
  reason: string;
  acknowledged: boolean;
  onAcknowledgedChange: (checked: boolean) => void;
  disabled?: boolean;
}

/** Requadre vermell amb el motiu i la casella obligatòria (§4 F.4). */
export function MoveLockNotice({ reason, acknowledged, onAcknowledgedChange, disabled }: MoveLockNoticeProps) {
  return (
    <div
      className="space-y-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900"
      role="alert"
      data-testid="move-lock-notice"
    >
      <p className="font-medium break-words">{moveLockNoticeText(reason)}</p>
      <label htmlFor="stay-move-lock-ack" className="flex items-center gap-2">
        <Checkbox
          id="stay-move-lock-ack"
          checked={acknowledged}
          onCheckedChange={onAcknowledgedChange}
          disabled={disabled}
          required
        />
        <span>{MOVE_LOCK_ACK_TEXT}</span>
      </label>
    </div>
  );
}

function unitLabel(u: Unit): string {
  return u.displayName || u.name;
}

/** Unitats actives de la propietat, més l'actual encara que sigui inactiva. */
export function selectableUnits(units: Unit[], currentUnitId?: string | null): Unit[] {
  return units
    .filter((u) => u.isActive || u.id === currentUnitId)
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || unitLabel(a).localeCompare(unitLabel(b)));
}

// ============================================
// QUERIES (mateixes claus que la fitxa i el calendari)
// ============================================

function useStaySource(reservationId: string, enabled: boolean) {
  return useQuery<StaySource>({
    queryKey: ['reservation-full', reservationId],
    queryFn: async () => {
      const res = await api.get(`/reservations/${reservationId}`);
      return res.data;
    },
    enabled,
  });
}

function usePropertyUnits(propertyId: number | null | undefined) {
  return useQuery<Unit[]>({
    queryKey: ['calendar-units', propertyId],
    queryFn: () => calendarApi.getUnits(propertyId ?? undefined),
    enabled: !!propertyId,
    staleTime: 1000 * 60 * 60,
  });
}

// ============================================
// FORM BODY
// ============================================

export interface EditStayFormProps {
  source: StaySource;
  units: Unit[];
  onSaved: () => void;
  onCancelled: () => void;
  /** El text amb què s'obre el camp de preu d'una reserva editable. Per defecte, el preu de la reserva. */
  initialPriceInput?: string;
}

export function EditStayForm({ source, units, onSaved, onCancelled, initialPriceInput }: EditStayFormProps) {
  const queryClient = useQueryClient();
  const isDirect = isDirectChannel(source.canal);
  const isCancelled = (source.estat ?? '').toUpperCase() === 'CANCELLED';
  const [motiu, setMotiu] = useState('');
  const [moveLockAck, setMoveLockAck] = useState(false);
  // El 409 del backend també el diu: per si la reserva s'ha bloquejat després de carregar la fitxa.
  const [lockFromError, setLockFromError] = useState<string | null>(null);
  const lockReason = source.metadata?.moveLockReason || lockFromError;

  const form = useForm<StayFormValues>({
    resolver: zodResolver(staySchemaFor(source)),
    defaultValues: {
      ...stayDefaults(source),
      ...(initialPriceInput !== undefined && isDirect && !isCancelled && { preu_total: initialPriceInput }),
    },
  });
  const { errors } = form.formState;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['reservation-full', source.id] });
    queryClient.invalidateQueries({ queryKey: ['reservation-metadata', source.id] });
  };

  // 409 (unitat ocupada) i 502 (Beds24 ha fallat): toast amb el missatge, i el diàleg obert.
  const saveMutation = useSaveStay({
    onSuccess: onSaved,
    onError: (error) => {
      const reason = moveLockReasonFromError(error.message);
      if (reason) setLockFromError(reason);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (reason: string) => {
      const body = reason.trim() ? { motiu: reason.trim() } : {};
      const res = await api.delete<CancelResponse>(`/reservations/${source.id}`, { data: body });
      return res.data;
    },
    onSuccess: (data) => {
      const result = stayCancelToast(data);
      toast[result.kind](result.title, { description: result.description, duration: 10000 });
      invalidate();
      onCancelled();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Hora de sortida pactada: només DB, mai Beds24. El toast (també el d'error) el fa el hook.
  const agreedMutation = useAgreedCheckoutMutation();

  const onSubmit = form.handleSubmit(async (values) => {
    const patch = buildStayPatch(source, values, isDirect);
    const agreed = stayAgreedCheckoutChange(source, values);
    const hasStayChanges = Object.keys(patch).length > 0;
    if (!hasStayChanges && !agreed) {
      toast.info('No hi ha cap canvi');
      return;
    }
    if (agreed) {
      try {
        await agreedMutation.mutateAsync({ reservationId: source.id, time: agreed.time });
      } catch {
        return; // El diàleg es queda obert amb el toast d'error.
      }
    }
    if (!hasStayChanges) {
      onSaved();
      return;
    }
    saveMutation.mutate({
      reservationId: source.id,
      patch: withMoveLockAck(patch, lockReason, moveLockAck),
    });
  });

  /**
   * Dates i preu d'una OTA (o d'una cancel·lada): camp desactivat i **no registrat**. Un input
   * registrat i `disabled` dona `undefined` a RHF i l'esquema tombaria l'enviament de la unitat;
   * sense registrar, el valor és el de `defaultValues`. `defaultValue` preomple també sense JS.
   */
  const lockableField = (name: 'data_entrada' | 'data_sortida' | 'preu_total') => {
    const initial = stayDefaults(source)[name];
    if (!isDirect || isCancelled) return { disabled: true, readOnly: true, value: initial };
    return {
      defaultValue: name === 'preu_total' && initialPriceInput !== undefined ? initialPriceInput : initial,
      ...form.register(name),
    };
  };

  // Ocupades aquelles dates: la mateixa font que el formulari de creació (`['calendar-bookings']`).
  const [watchedIn, watchedOut, watchedUnit, watchedPrice] = useWatch({
    control: form.control,
    name: ['data_entrada', 'data_sortida', 'unit_id', 'preu_total'],
  });
  // Sota el preu, en viu: l'error de decimals (desactiva «Desar») i l'avís de l'IVA (no bloqueja).
  const priceFeedback = stayPriceFeedback(source, watchedPrice);
  const priceError = priceFeedback.error ?? errors.preu_total?.message;
  // `loaded: false` = les dates surten del que la graella té carregat (un mes enrere, tretze
  // endavant) o la fitxa s'ha obert sense graella: cap unitat surt «ocupada» i cal dir-ho.
  const { occupied, loaded } = useLoadedOccupancy(
    watchedIn ?? '',
    watchedOut ?? '',
    source.beds24_property_id,
    source.id,
  );
  const occupancyUnknown = !loaded && !!watchedIn && !!watchedOut && !isCancelled;
  const options = buildUnitOptions(selectableUnits(units, source.unit_id), occupied, watchedUnit);
  const busy = saveMutation.isPending || cancelMutation.isPending || agreedMutation.isPending;
  const initialStay = stayDefaults(source);
  const unitChanged = (watchedUnit ?? initialStay.unit_id) !== initialStay.unit_id;
  const notice = stayNoticeFor(
    (watchedIn ?? initialStay.data_entrada) !== initialStay.data_entrada ||
      (watchedOut ?? initialStay.data_sortida) !== initialStay.data_sortida,
    unitChanged,
  );
  const moveLock = moveLockAckState(lockReason, unitChanged, moveLockAck);

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p>{notice}</p>
      </div>

      {/* Una columna a pantalla estreta: a 385 px el camp de data de «Sortida» es desbordava. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="min-w-0 space-y-1.5">
          <Label htmlFor="stay-data-entrada">Entrada</Label>
          <Input
            id="stay-data-entrada"
            type="date"
            className="w-full min-w-0"
            {...lockableField('data_entrada')}
          />
          {errors.data_entrada && (
            <p className="text-xs text-destructive">{errors.data_entrada.message}</p>
          )}
        </div>
        <div className="min-w-0 space-y-1.5">
          <Label htmlFor="stay-data-sortida">Sortida</Label>
          <Input
            id="stay-data-sortida"
            type="date"
            className="w-full min-w-0"
            {...lockableField('data_sortida')}
          />
          {errors.data_sortida && (
            <p className="text-xs text-destructive">{errors.data_sortida.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="stay-unit">Habitació</Label>
        <Controller
          control={form.control}
          name="unit_id"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange} disabled={isCancelled}>
              <SelectTrigger id="stay-unit">
                <SelectValue placeholder="Tria una unitat" />
              </SelectTrigger>
              {/* La fitxa és un Sheet no modal: sense el marcador, un clic aquí la tancaria. */}
              <SelectContent data-sheet-outside-safe="">
                {options.map((u) => (
                  <SelectItem key={u.id} value={u.id} disabled={u.disabled}>
                    {u.label}
                    {u.hint && <span className="ml-1 text-xs text-muted-foreground">· {u.hint}</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {occupancyUnknown && <OccupancyNotLoadedNotice when="desar" />}
      </div>

      {moveLock.required && lockReason && (
        <MoveLockNotice
          reason={lockReason}
          acknowledged={moveLockAck}
          onAcknowledgedChange={setMoveLockAck}
          disabled={busy}
        />
      )}

      <div className="space-y-1.5">
        <Label htmlFor="stay-preu-total">Preu total (€)</Label>
        <Input
          id="stay-preu-total"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          {...lockableField('preu_total')}
        />
        {priceError && <p className="text-xs text-destructive">{priceError}</p>}
        <PriceVatNotice
          notice={priceFeedback.notice}
          disabled={busy}
          onPick={(cents) =>
            form.setValue('preu_total', centsToInputValue(cents), { shouldDirty: true, shouldValidate: true })
          }
        />
      </div>

      {!isDirect && <p className="text-xs text-muted-foreground">Dates i preu: {OTA_LOCKED_TEXT}.</p>}

      <div className="space-y-1.5">
        <Label htmlFor="stay-agreed-checkout">Hora de sortida pactada</Label>
        <Controller
          control={form.control}
          name="agreed_checkout"
          render={({ field }) => (
            <Select
              value={field.value ?? AGREED_CHECKOUT_NONE}
              onValueChange={field.onChange}
              disabled={isCancelled || busy}
            >
              <SelectTrigger id="stay-agreed-checkout">
                <SelectValue />
              </SelectTrigger>
              {/* La fitxa és un Sheet no modal: sense el marcador, un clic aquí la tancaria. */}
              <SelectContent data-sheet-outside-safe="">
                {agreedCheckoutOptions(source.agreedCheckoutTime).map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        <p className="text-xs text-muted-foreground">{AGREED_CHECKOUT_HINT}</p>
      </div>

      <DialogFooter className="gap-2 sm:justify-between">
        {isDirect && !isCancelled ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button type="button" variant="outline" className="text-destructive" disabled={busy}>
                Cancel·lar la reserva
              </Button>
            </AlertDialogTrigger>
            {/* Mateixos marges i cantonades que la resta de diàlegs (`DialogContent`), també a mòbil. */}
            <AlertDialogContent
              className="w-full max-w-[calc(100%-2rem)] rounded-lg sm:max-w-lg"
              data-sheet-outside-safe=""
            >
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel·lar la reserva?</AlertDialogTitle>
                <AlertDialogDescription>
                  Es cancel·la al PMS i a Beds24, i es retiren els codis dels panys. No es pot desfer
                  des d'aquí.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-1.5">
                <Label htmlFor="stay-cancel-motiu">Motiu (opcional)</Label>
                <Textarea
                  id="stay-cancel-motiu"
                  value={motiu}
                  onChange={(e) => setMotiu(e.target.value)}
                  rows={2}
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={cancelMutation.isPending}>Enrere</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={cancelMutation.isPending}
                  onClick={() => cancelMutation.mutate(motiu)}
                >
                  {cancelMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Cancel·lar la reserva
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <span />
        )}
        <Button type="submit" disabled={busy || isCancelled || moveLock.blocksSave || !!priceFeedback.error}>
          {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Desar
        </Button>
      </DialogFooter>
    </form>
  );
}

// ============================================
// DIALOG
// ============================================

export interface EditStayDialogProps {
  reservationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Després de cancel·lar: la fitxa es tanca. */
  onCancelled: () => void;
}

export function EditStayDialog({ reservationId, open, onOpenChange, onCancelled }: EditStayDialogProps) {
  const { data: source, isLoading } = useStaySource(reservationId, open);
  const { data: units = [], isLoading: loadingUnits } = usePropertyUnits(source?.beds24_property_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* La fitxa és un Sheet no modal: sense el marcador, un clic dins del diàleg la tancaria. */}
      <DialogContent className="sm:max-w-md" data-sheet-outside-safe="">
        <DialogHeader>
          <DialogTitle>Canviar dates, habitació o preu</DialogTitle>
          <DialogDescription>Els camps que no toquis no s'envien.</DialogDescription>
        </DialogHeader>
        {isLoading || loadingUnits || !source ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <EditStayForm
            key={source.id}
            source={source}
            units={units}
            onSaved={() => onOpenChange(false)}
            onCancelled={() => {
              onOpenChange(false);
              onCancelled();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// BUTTON (el que pinta la fitxa)
// ============================================

export interface EditStayButtonProps {
  reservationId: string;
  onCancelled: () => void;
}

/** Botó + diàleg. Només ADMIN: per a la resta no pinta res. */
export function EditStayButton({ reservationId, onCancelled }: EditStayButtonProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  if (user?.role !== 'ADMIN') return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="w-full justify-start gap-2"
        onClick={() => setOpen(true)}
      >
        <CalendarClock className="h-4 w-4" />
        Canviar dates, habitació o preu
      </Button>
      <EditStayDialog
        reservationId={reservationId}
        open={open}
        onOpenChange={setOpen}
        onCancelled={onCancelled}
      />
    </>
  );
}
